import { ENDPOINTS } from 'config/endpoints';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { ResponsePaginated } from 'crono-fe-common/types/response';
import { TemplateSearch } from 'crono-fe-common/types/DTO/templateSearch';
import { Template } from 'crono-fe-common/types/template';

export default function useSearchTemplates(searchParameters: TemplateSearch) {
  const request: Request = {
    url: `${ENDPOINTS.template.search}`,
    config: {
      method: 'post',
      data: { data: searchParameters },
    },
  };
  const stringifiedSearchParameters = JSON.stringify(searchParameters);
  const requestConfig = {
    queryKey: [ENDPOINTS.template.search, stringifiedSearchParameters],

    queryFn: () => client(request),
  };

  return useQuery<ResponsePaginated<Template[]>, CronoError>(requestConfig);
}

export function useSearchTemplatesInfiniteScroll(
  searchParameters: Omit<TemplateSearch, 'offset'>,
) {
  const requestConfig = {
    queryKey: [
      ENDPOINTS.template.search,
      'infinite',
      JSON.stringify(searchParameters),
    ],
    queryFn: ({ pageParam = 0 }) => {
      const request: Request = {
        url: ENDPOINTS.template.search,
        config: {
          method: 'post',
          data: { data: { ...searchParameters, offset: pageParam } },
        },
      };
      return client(request);
    },
    getNextPageParam: (
      lastPage: ResponsePaginated<Template[]>,
      pages: ResponsePaginated<Template[]>[],
    ) => {
      const howManyLoaded = pages.reduce(
        (sum, page) => sum + page.data.data.length,
        0,
      );

      const checkIfHasNextPage =
        lastPage.data.data.length === 0 ||
        lastPage.data.data.length < searchParameters.limit;

      if (checkIfHasNextPage) {
        return undefined;
      }

      return howManyLoaded;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  };

  return useInfiniteQuery<ResponsePaginated<Template[]>, CronoError>(
    requestConfig,
  );
}
